import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  LoadingSkeleton,
  RadioButtons,
} from "@narmi/design_system";
import { useLocalization, Localized } from "@fluent/react";
import { fetchSudoCode, fetchSudoDevices } from "./utils";
import HeaderWithSteps from "../HeaderWithSteps";
import { useSudoContext } from "./SudoContext";
import Container from "../Container";
import BackButton from "./BackButton";
import ActionBar from "./ActionBar";

import styles from "./RequestOTP.module.scss";
import buttonStyles from "./ActionBar.module.scss";

interface RequestOTPProps {
  isInDialog?: boolean;
}

/** Whether a `POST /v1/sudo` rejection is the 2FA challenge carrying the device list. */
const isDeviceListResponse = (
  response: unknown
): response is { devices: { name: string; id: string }[] } =>
  !!response &&
  typeof response === "object" &&
  (response as { id?: unknown }).id === "two_factor_authentication_required" &&
  Array.isArray((response as { devices?: unknown }).devices);

/** Reports a failed device-list fetch to Sentry. */
const reportDeviceLoadFailure = (rejection: unknown) => {
  window.Sentry?.captureMessage("Sudo device list failed to load", {
    level: "warning",
    extra: { rejection },
    tags: { feature: "sudo" },
  });
};

const RequestOTP = ({ isInDialog }: RequestOTPProps = {}) => {
  const { originalRoute, submitOtpRoute, onCancel, closeRouteOverride } =
    useSudoContext();
  const navigate = useNavigate();
  const { l10n } = useLocalization();
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>("");
  const [loadingDevices, setLoadingDevices] = useState(true);
  const [devices, setDevices] = useState<{ name: string; id: string }[]>([]);
  // `null` when there is no error; otherwise the server's message, if it sent one.
  const [loadError, setLoadError] = useState<{ message: string | null } | null>(
    null
  );

  const loadDevices = useCallback(() => {
    setLoadingDevices(true);
    setLoadError(null);
    // There are 3 uses for the API:
    // 1. Check if Sudo has been established with a GET
    // 2. Get the possible Sudo devices with a POST - this is what we're doing here it will return a 403
    // 3. Establish Sudo with a POST while passing a device ID
    return fetchSudoDevices()
      .then(() => {
        // Unreachable today: without a device_id this endpoint always answers
        // 403. Show the error state rather than the "add a device" fallback.
        setLoadError({ message: null });
      })
      .catch((response) => {
        if (!isDeviceListResponse(response)) {
          reportDeviceLoadFailure(response);
          // `ApiHttp` rejects with the server's message as a string, e.g. a 429.
          setLoadError({
            message: typeof response === "string" && response ? response : null,
          });
          return;
        }
        if (response.devices.length === 1) {
          setSelectedDeviceId(response.devices[0].id);
        }
        setDevices(response.devices);
      })
      .finally(() => {
        setLoadingDevices(false);
      });
  }, []);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  const sendDevice = async () => {
    if (!selectedDeviceId) {
      return;
    }

    const message = await fetchSudoCode(selectedDeviceId);

    navigate(submitOtpRoute, {
      state: { selectedDeviceId, helpMessage: message },
    });
  };

  const subheader = (
    /*
    For most flows that use HeaderWithSteps, spacing b/t header and subheader is 8px on l/m, 4px on s/xs.
    For sudo, it's always 4px, so we need to subtract 4px here
    */
    <div style={{ marginTop: "-4px" }}>
      {l10n.getString("two-factor-flow-message")}
    </div>
  );

  const backButton = (
    <BackButton
      to={closeRouteOverride || originalRoute || "/"}
      label={l10n.getString("button-back", null, "Back")}
      onClick={onCancel}
    />
  );

  let content;
  if (devices?.length) {
    content = (
      <>
        <div>
          <RadioButtons
            name="deviceSelect"
            options={Object.fromEntries(devices.map((d) => [d.name, d.id]))}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSelectedDeviceId(e.target.value)
            }
            value={selectedDeviceId}
          />
        </div>
        <ActionBar align={isInDialog ? "right" : "center"}>
          {backButton}
          <div className={buttonStyles.continueButtonWrapper}>
            <Button
              testId="send-otp-button"
              label={l10n.getString("button-continue", null, "Continue")}
              onClick={sendDevice}
            />
          </div>
        </ActionBar>
      </>
    );
  } else if (loadError) {
    content = (
      <div data-testid="devices-load-error">
        <Alert isActive isDismissable={false} kind="error">
          {loadError.message ||
            l10n.getString("error-loading-two-factor-devices")}
        </Alert>
        <ActionBar align={isInDialog ? "right" : "center"}>
          {backButton}
          <div className={buttonStyles.continueButtonWrapper}>
            <Button
              testId="retry-devices-button"
              label={l10n.getString("button-try-again")}
              onClick={loadDevices}
            />
          </div>
        </ActionBar>
      </div>
    );
  } else {
    content = (
      <div>
        <Localized
          id="message-no-devices"
          elems={{
            settingsButton: (
              <Button
                kind="plain"
                testId="no-devices-message-button"
                onClick={() => window.location.assign("/settings/security")}
                label={l10n.getString("button-settings", null, "Settings")}
              />
            ),
          }}
        >
          <span data-testid="settings-message">
            Please go to your{" "}
            <Button
              kind="plain"
              testId="settings-message-button"
              onClick={() => window.location.assign("/settings/security")}
              label={l10n.getString("button-settings", null, "Settings")}
            />{" "}
            and add a two factor authentication device.
          </span>
        </Localized>
        <ActionBar align={isInDialog ? "right" : "center"}>
          {backButton}
        </ActionBar>
      </div>
    );
  }

  return (
    <LoadingSkeleton isLoading={loadingDevices}>
      {isInDialog ? (
        <>
          <div className={styles.subheaderText}>{subheader}</div>
          {content}
        </>
      ) : (
        <Container>
          <HeaderWithSteps
            headerText={l10n.getString(
              "heading-verify-account",
              null,
              "Let's verify your account"
            )}
            subheaderText={subheader}
          />
          {content}
        </Container>
      )}
    </LoadingSkeleton>
  );
};

export default RequestOTP;
